/**
 * URL builder utilities for external navigation
 */

const getEventHistoryDateRange = (daysBack: number, nowMs = Date.now()) => {
  const toDate = new Date(nowMs);
  toDate.setHours(23, 59, 59, 999);

  const fromDate = new Date(toDate);
  fromDate.setDate(fromDate.getDate() - daysBack);
  fromDate.setHours(0, 0, 0, 0);

  return { fromDateMs: fromDate.getTime(), toDateMs: toDate.getTime() };
};

export const buildLegacyEventHistoryUrl = (baseUrl: string): string =>
  baseUrl ? `${baseUrl}/yms/eventHistory#/eventReport` : "";

/**
 * Builds an Event History report URL with date range
 * @param baseUrl - Base URL for Event History
 * @param visitId - Visit ID to query
 * @param buildingCode - Building/yard code
 * @param daysBack - Number of days to look back (default: 175)
 * @returns Formatted Event History URL
 */
export const buildEventHistoryUrl = (
  baseUrl: string,
  visitId: string,
  buildingCode: string,
  daysBack = 175
): string => {
  const { fromDateMs, toDateMs } = getEventHistoryDateRange(daysBack);

  return `${baseUrl}?visitId=${visitId}&yard=${buildingCode}&fromDate=${fromDateMs}&toDate=${toDateMs}`;
};

export type EventHistoryIdentifierType = "VRID" | "ISA" | "LP" | "EQUIPMENT";

interface BuildEventHistoryIdentifierUrlOptions {
  baseUrl: string;
  buildingCode: string;
  identifier: string;
  identifierType: EventHistoryIdentifierType;
  vehicleNumber?: string;
  nowMs?: number;
  daysBack?: number;
}

export const buildEventHistoryIdentifierUrl = ({
  baseUrl,
  buildingCode,
  identifier,
  identifierType,
  vehicleNumber,
  nowMs = Date.now(),
  daysBack = 14,
}: BuildEventHistoryIdentifierUrlOptions): string => {
  if (!baseUrl || !buildingCode || !identifier) return "";

  const { fromDateMs, toDateMs } = getEventHistoryDateRange(daysBack, nowMs);
  const query = new URLSearchParams({
    yard: buildingCode,
    fromDate: String(fromDateMs),
    toDate: String(toDateMs),
  });

  if (identifierType === "LP") {
    query.set("licensePlateNumber", identifier);
  } else if (identifierType === "EQUIPMENT") {
    query.set("yardAssetId", identifier);
    if (vehicleNumber) query.set("vehicleNumber", vehicleNumber);
  } else {
    query.set("loadIdentifierType", identifierType);
    query.set("loadIdentifier", identifier);
  }

  return `${baseUrl}?${query.toString()}`;
};

/**
 * Builds an RTT (Track & Trace) Timeline URL
 * @param baseUrl - Base URL for RTT
 * @param regionId - Region ID (NA, EU, JP)
 * @param equipmentNumber - Equipment number to track
 * @returns Formatted RTT Timeline URL
 */
export const buildRTTTimelineUrl = (
  baseUrl: string,
  regionId: string,
  equipmentNumber: string
): string => {
  return `${baseUrl}/view/${regionId}:EQUIPMENT:${equipmentNumber}?m=asset&status=ACTIVE`;
};

/**
 * Locale segment DockMaster 3.0 expects, per realm. One code per realm rather
 * than the viewer's UI language: the segment picks the DockMaster UI language,
 * and this app serves 19 locales that DM3 does not, so threading `i18n.language`
 * through would emit segments DM3 cannot honour. Realm is the same input that
 * selects the DM3 base, so base and locale always agree.
 */
const DOCK_MASTER_LOCALES: Record<string, string> = {
  USAmazon: "en_US",
  EUAmazon: "en_GB",
  JPAmazon: "ja_JP",
};

/** Locale for a realm absent from the map — a new realm must not emit `undefined`. */
const DEFAULT_DOCK_MASTER_LOCALE = "en_US";

/**
 * Builds a DockMaster 3.0 appointment-detail URL.
 *
 * DM3 replaced the legacy `fc-inbound-dock-hub-*.aka.amazon.com` DockHub site,
 * which is being turned off. The route shape changed with it: no `#` hash, no
 * `dockmaster` segment, and the FC moves ahead of `appointment`. The sub-page
 * name (`appointmentDetail`) is preserved. Bases live in `REDIRECT_LINKS`.
 *
 * @param isaId - ISA appointment ID
 * @param buildingCode - Building code for the appointment
 * @param baseUrl - Regional DM3 base URL
 * @param realm - Realm (USAmazon, EUAmazon, JPAmazon); selects the locale segment
 * @returns Formatted DM3 appointment detail URL, or "" when an input is missing
 */
export const buildDockMasterUrl = (
  isaId: string,
  buildingCode: string,
  baseUrl: string,
  realm: string
): string => {
  if (!isaId || !buildingCode || !baseUrl) return "";

  // Extract first 17 characters of ISA ID
  const truncatedIsaId = isaId.substring(0, 17);

  const locale = DOCK_MASTER_LOCALES[realm] ?? DEFAULT_DOCK_MASTER_LOCALE;

  // Example: https://prod-na.us-east-1.idx-website.aft.amazon.dev/en_US/BFI4/appointment/view/1763459696628/appointmentDetail
  return `${baseUrl}/${locale}/${buildingCode}/appointment/view/${truncatedIsaId}/appointmentDetail`;
};

/**
 * Builds a Dock Master VRID URL
 * @param vrId - VR ID
 * @param buildingCode - Building code for the appointment (optional)
 * @param baseUrl - Base Dock Master URL for the region
 * @param appointmentType - Appointment type (INBOUND or OUTBOUND)
 * @returns Formatted Dock Master VRID URL
 */
export const buildDockMasterVridUrl = (
  vrId: string,
  buildingCode: string,
  baseUrl: string,
  appointmentType: string
): string => {
  if (!vrId || !baseUrl) return "";

  // Extract first 17 characters of VRID
  const truncatedVrId = vrId.substring(0, 17);

  // Determine direction: ib for INBOUND, ob for OUTBOUND
  const direction = appointmentType === "INBOUND" ? "ib" : "ob";

  // Build full URL
  // Example: https://preprod-trans-logistics.amazon.com/ssp/dock/hrz/ob?vrid=115LG66YQ
  // Optional: Add setNodeId if buildingCode is provided
  const setNodeParam = buildingCode ? `setNodeId=${buildingCode}&` : "";
  return `${baseUrl}/ssp/dock/hrz/${direction}?${setNodeParam}vrid=${truncatedVrId}`;
};

export const buildSspDockScheduleUrl = (baseUrl: string, buildingCode: string): string => {
  if (!baseUrl || !buildingCode) return "";

  const query = new URLSearchParams({ setNodeId: buildingCode });
  return `${baseUrl}/ssp/dock/hrz/schedule?${query.toString()}`;
};

/**
 * Builds an FMC (Fleet Management Console) VRID execution-search URL.
 * @param vrId - VR ID (used as the final path segment, untruncated)
 * @param baseUrl - Base trans-logistics URL for the region (RYC's TCP_BASEURL)
 * @returns Formatted FMC execution-search URL, or "" when inputs are missing
 */
export const buildFmcVridSearchUrl = (vrId: string, baseUrl: string): string => {
  if (!vrId || !baseUrl) return "";

  // Example: https://trans-logistics.amazon.com/fmc/execution/search/115LG66YQ
  return `${baseUrl}/fmc/execution/search/${encodeURIComponent(vrId)}`;
};

interface BuildRttVridTripUrlOptions {
  baseUrl: string;
  regionId: string;
  vrId: string;
}

export const buildRttVridTripUrl = ({
  baseUrl,
  regionId,
  vrId,
}: BuildRttVridTripUrlOptions): string => {
  if (!baseUrl || !regionId || !vrId) return "";

  const query = new URLSearchParams({
    m: "trip",
    preset: "today",
    column: "scheduled_end",
    type: "vehicleRun",
    q: vrId,
    dateField: "effectiveEnd",
  });
  return `${baseUrl}/view/${regionId}:VR:${encodeURIComponent(vrId)}?${query.toString()}`;
};
